import apiWallpaperWeb from "@utils/api/api_wallpaper_web";
import type { LogEventInput } from "@utils/api/analytics/log-system.api";
import type { UserLoginPageDTO } from "@utils/api/analytics/user-event.dto";

/** /wallpaper/web-client/login 的回應（攔截器已回傳 res.data）。 */
export interface WebClientLoginResponse {
	access_token: string;
	expires_at: string;
	token_type: string;
	[property: string]: unknown;
}

/** 裝置 uuid：第一次進站產生後存下來重複使用，後端用它辨識同一個 web client。 */
const UUID_KEY = "wallpaper_web_uuid";
const TOKEN_KEY = "wallpaper_web_token";
const TOKEN_TYPE_KEY = "wallpaper_web_token_type";
const TOKEN_EXPIRES_KEY = "wallpaper_web_token_expires_at";

function readStore(key: string): string | null {
	try {
		return localStorage.getItem(key);
	} catch {
		return null;
	}
}

function writeStore(key: string, value: string): void {
	try {
		localStorage.setItem(key, value);
	} catch {
		// 無痕 / 停用儲存時忽略（此次 session 仍可用記憶體中的 token 打完流程）
	}
}

function createUuid(): string {
	if (typeof crypto !== "undefined" && crypto.randomUUID) {
		return crypto.randomUUID();
	}
	// 舊瀏覽器 / 非安全來源沒有 randomUUID，手動組 v4 格式
	return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
		const r = Math.floor(Math.random() * 16);
		return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
	});
}

/**
 * 登入與數據上報的 timeout：不設的話網路卡住會無限等待，把等它的流程一起卡住。
 * 上傳圖片刻意不設（圖檔較大、慢速網路下需要時間）。
 */
const REQUEST_TIMEOUT_MS = 10000;

// 併發的 log / upload 只會觸發一次登入
let loginPromise: Promise<void> | null = null;

/**
 * 桌布活動「國際 web 版」的 API（打 REACT_APP_API_URL）。
 *
 * 國際版沒有 LINE 身分，改用裝置 uuid 換 access_token；
 * 上傳圖片與數據上報都帶這個 token（取代原本的 X-Signature 與假的數據登入參數）。
 */
export namespace WallpaperWebApi {
	/** 取得（必要時產生）此裝置的 uuid。 */
	export function getUuid(): string {
		const stored = readStore(UUID_KEY);
		if (stored) return stored;
		const uuid = createUuid();
		writeStore(UUID_KEY, uuid);
		return uuid;
	}

	/** 是否已有尚未過期的 token（expires_at 解析不出來時當作有效，改由 401 觸發重換）。 */
	export function hasValidToken(): boolean {
		if (!readStore(TOKEN_KEY)) return false;
		const expiresAt = readStore(TOKEN_EXPIRES_KEY);
		if (!expiresAt) return true;
		const expiresTs = Date.parse(expiresAt);
		return Number.isNaN(expiresTs) ? true : expiresTs > Date.now();
	}

	/** 用裝置 uuid 換發 access_token（強制重換）。 */
	export async function login(): Promise<void> {
		const res = (await apiWallpaperWeb.post(
			"/wallpaper/web-client/login",
			{ uuid: getUuid() },
			{ timeout: REQUEST_TIMEOUT_MS },
		)) as unknown as WebClientLoginResponse;

		if (!res?.access_token) {
			throw new Error("[wallpaper-web-login] 回傳沒有 access_token");
		}
		writeStore(TOKEN_KEY, res.access_token);
		writeStore(TOKEN_TYPE_KEY, res.token_type || "Bearer");
		writeStore(TOKEN_EXPIRES_KEY, res.expires_at ?? "");
	}

	/** 沒有有效 token 才登入；同時多處呼叫只會打一次 login。 */
	export async function ensureToken(): Promise<void> {
		if (hasValidToken()) return;
		if (!loginPromise) {
			loginPromise = login().finally(() => {
				loginPromise = null;
			});
		}
		await loginPromise;
	}

	/** 上傳合成好的桌布圖（multipart/form-data，欄位名 image）。 */
	export async function upload(formData: FormData) {
		await ensureToken();
		return apiWallpaperWeb.post("/wallpaper/upload", formData);
	}

	export async function logPage(data: UserLoginPageDTO) {
		await ensureToken();
		return apiWallpaperWeb.post("/log/page", data, {
			timeout: REQUEST_TIMEOUT_MS,
		});
	}

	export async function logEvent(data: LogEventInput) {
		await ensureToken();
		return apiWallpaperWeb.post("/log/event", data, {
			timeout: REQUEST_TIMEOUT_MS,
		});
	}
}
